/**
 * EntitySwitcher — top-bar "current entity" context selector (D-073).
 *
 * App-wide source of truth for the entity the user is currently working on
 * (authProvider get/set/useCurrentEntityId). Turned into a real, always-visible
 * control (it was previously an easy-to-miss text button):
 *  - prominent pill trigger shows the working entity + its type ("AlphaKey - Product");
 *  - "My entities" lists everything the user can work on (companies included);
 *  - the active entity row is highlighted; "+ New company" sits at the bottom and opens
 *    the shared Wizard_Entity_Onboarding, auto-selecting the freshly created entity.
 *
 * Renders at the left of the dashboard top bar (and is reused inline by flows that need to
 * confirm the entity — e.g. Investor Outreach step 1.1). The left sidebar is untouched.
 *
 * ONE selector, always a DROPDOWN (PO revert 2026-09-11): the multi-company "rail" + the
 * portfolio card grid added by P127 T103/T108 ("one button per entity") turned the top bar
 * into a wall of controls. Reverted to the single dropdown. What SURVIVES from that work is
 * the load-bearing part — the real outstanding count as the shared `NeedsYouBadge` red dot:
 * a leading indicator the user follows without having to remember where the work is.
 * Counts come from /harness/portfolio (real rows, rule 0c); zero = no badge, never a fake 0.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, ChevronDown, Check, Plus, Loader2, AlertCircle, RotateCcw } from "lucide-react";
import { NeedsYouBadge } from "@/components/ui/NeedsYouBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { listEntities, type EntityData } from "@/services/api/entityApi";
import { isQuiet, useAttentionCounts, useAttentionTotal } from "@/hooks/useAttentionCounts";
import {
  getUserEntityId,
  setCurrentEntityId,
  useCurrentEntityId,
} from "@/providers/firebase/authProvider";
import { Wizard_Entity_Onboarding } from "@/components/wizard/Wizard_Entity_Onboarding";
// Centralised entity identity (labels/dots) — shared with Investor Outreach.
import { EntityTypeDot, entityTypeLabel } from "@/components/entity/entityType";

export function EntitySwitcher() {
  const [entities, setEntities] = useState<EntityData[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentEntityId = useCurrentEntityId();
  const userEntityId = getUserEntityId();

  const loadEntities = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listEntities()
      .then((list) => {
        if (!cancelled) setEntities(list);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load entities");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * A COMPANY ADOPTED AFTER THIS MOUNT HAS TO REACH THE LIST (2026-09-28, PO: *"the select entity on top
   * left is not selected !?"*). The list was fetched ONCE on mount, and the one flow that changes the
   * working company without the wizard is the doorway's claim: `EmailPage` calls
   * `setCurrentEntityId(res.entityId)` the moment his five land, and that company did not exist when this
   * list was read. `current` then resolved to null and the pill fell back to its "Select entity"
   * placeholder WHILE a company was selected — the reader was told nothing was chosen, on the one control
   * whose whole job is to say what he is working on. The tell was beside it in his own screenshot: the
   * mailbox read "— Raisy", because THAT surface re-reads on this same dependency (EmailPage's own
   * `listEntities` effect is keyed on `currentEntityId`), and the two disagreed until this line existed.
   *
   * WHY A RE-READ RATHER THAN A GUARD: it is the SAME dependency its sibling reader uses, so one rule now
   * governs both. A switch re-reads a list that already contains the target, so the pill never blinks; and
   * an id this account's list cannot contain (a stale `raisy_current_entity_id` from another account)
   * cannot loop, because nothing here depends on the answer.
   */
  useEffect(() => {
    const cleanup = loadEntities();
    return cleanup;
  }, [loadEntities, currentEntityId]);

  // The count comes from the ONE attention store (P144 D-144-001): a shared react-query key, so
  // this switcher, the sidebar rows and the email tab share ONE request rather than each polling
  // the same endpoint (P144 IN-01 — the three-pollers defect, removed by construction). An absent
  // count renders no badge: an honest absence, never a fabricated zero.
  const { counts } = useAttentionCounts();

  // P144 T0402 / IN-04 — THE TOP-DOWN SUM, and it is not optional: "it should be visible we are
  // always thinking top down". It is the Σ of the same rows the per-entity badges render, so the
  // two can never disagree; the PO's own accepted form was "outside (/XX)", and the slash is
  // replaced by the WORD `all` because a bare slash asks the reader to work out which number is
  // his on every single glance (P144 §STEP 6, C1). Never red: red is reserved for "this is on me
  // now", and a second red number beside the first would make both unreadable (S1).
  const totalOutstanding = useAttentionTotal();

  const sorted = useMemo(
    () =>
      [...entities].sort((a, b) =>
        (a.entity_name ?? "").localeCompare(b.entity_name ?? ""),
      ),
    [entities],
  );

  const current = sorted.find((e) => e.entity_id === currentEntityId) ?? null;
  const currentIsUser = currentEntityId === userEntityId;
  // The type reads inline as a hyphen suffix after the name: "AlphaKey - Product".
  const currentType = current ? (currentIsUser ? "You" : entityTypeLabel(current.entity_type)) : null;

  // PO rule (2026-09-21): any preparation or question is a blocker and must be marked with the
  // ONE red dot, SUMMARIZED. The dot's noun names what is waiting, so a prepared draft reads as
  // a preparation and not as an anonymous number. One dot, one count, one channel — a second
  // badge would only teach the user to ignore both (design-implication 8).
  const badgeNoun = (c?: { total: number; preparation: number }) => {
    if (!c || c.preparation <= 0) return "need you";
    const prepared = c.preparation === 1 ? "1 prepared draft" : `${c.preparation} prepared drafts`;
    return `need you · ${prepared}`;
  };

  const renderRow = (e: EntityData) => {
    const active = e.entity_id === currentEntityId;
    return (
      <DropdownMenuItem
        key={e.entity_id}
        data-testid={`entity-row-${e.entity_id}`}
        onClick={() => setCurrentEntityId(e.entity_id)}
        className={`flex items-center gap-2 ${
          active ? "bg-[#0AB3BF]/10 text-slate-900 dark:text-white" : ""
        }`}
      >
        <EntityTypeDot type={e.entity_type} />
        <span className={`truncate ${active ? "font-semibold" : ""}`}>
          {e.entity_name || e.entity_id}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {/* The red dot that says "this company has work waiting" — real count or nothing. */}
          <NeedsYouBadge
            count={counts[e.entity_id]?.total ?? 0}
            noun={badgeNoun(counts[e.entity_id])}
            // THE QUIET STATE AT THIS HOME TOO (P144 D-144-019). The company's own count now has
            // two homes — this row and the Entity Review row in the rail — and they are the same
            // number, so they must answer "have I opened all of this?" the same way. `isQuiet`
            // refuses to dim unless the part with view facts IS the whole number, so this row can
            // never claim "you have looked" over a count made of drafts.
            dimmed={isQuiet(
              counts[e.entity_id]?.total,
              counts[e.entity_id]?.processGateOpened,
              counts[e.entity_id]?.processGate,
            )}
          />
          {e.entity_id === userEntityId && (
            <span className="text-label-2xs uppercase tracking-wide text-muted-foreground/70">
              You
            </span>
          )}
          {active && <Check className="h-3.5 w-3.5 shrink-0 text-[#0AB3BF]" />}
        </span>
      </DropdownMenuItem>
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="entity-switcher-pill"
            className={`group flex max-w-72 items-center gap-2 rounded-lg border px-2 py-1 text-left transition-colors ${
              current
                ? "border-[#0AB3BF]/50 bg-[#0AB3BF]/10 hover:border-[#0AB3BF]"
                : "border-dashed border-border hover:border-[#0AB3BF]/60 hover:bg-[#0AB3BF]/5"
            }`}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#0AB3BF] text-white">
              <Building2 className="h-3.5 w-3.5" />
            </span>
            <span className="flex min-w-0 items-baseline gap-1 text-sm font-semibold text-slate-900 dark:text-white">
              <span className="truncate">{current?.entity_name ?? "Select entity"}</span>
              {currentType && (
                <span className="shrink-0 text-xs font-medium text-muted-foreground/70">
                  {` - ${currentType}`}
                </span>
              )}
            </span>
            {/* NOTHING SITS HERE ANY MORE (PO 2026-09-28: *"ITS CONFUSING … its the other way
                around"*). This control names ONE company, so any number on it has to be about that
                company — and a number about one company belongs where that company is chosen. The
                portfolio total moved OUT to the sibling position below; the company's own count
                keeps two homes that are each about ONE company: its row in the dropdown, and the
                Entity Review row in the rail. */}
            {loading && (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground/70" />
            )}
            {!loading && error && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                      <AlertCircle className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">{error}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-colors group-hover:text-[#0AB3BF]" />
          </button>
        </DropdownMenuTrigger>
        {/* IN-04's all-companies sum — where the red dot used to sit, OUTSIDE the trigger, and
            still read without a click. Neutral because it is not "on me now": it counts every
            company, and the word after the number says exactly that, so nobody has to guess what
            the grey means (PO 2026-09-28: *"call it '60 total'"… *"the colour is grey and nobody
            knows what that means?"*).
            A NEUTRAL COUNT IS NOT A DOOR: no surface lists every company's items at once, so this
            stays a display — the door for the company you are in is its rail row. */}
        <span data-testid="attention-pill-all" className="shrink-0">
          <NeedsYouBadge
            count={totalOutstanding}
            tone="neutral"
            label="total"
            labelPosition="trailing"
            noun="across all your companies"
          />
        </span>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground/70">
            My entities
          </DropdownMenuLabel>
          {loading && sorted.length === 0 && (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground/70">
              <Loader2 className="mx-auto mb-1 h-4 w-4 animate-spin" />
              Loading entities…
            </div>
          )}
          {!loading && error && sorted.length === 0 && (
            <div className="px-2 py-3 text-center">
              <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  loadEntities();
                }}
                className="inline-flex items-center gap-1 rounded-md bg-[#0AB3BF]/10 px-2 py-1 text-xs font-medium text-[#0AB3BF] hover:bg-[#0AB3BF]/20"
              >
                <RotateCcw className="h-3 w-3" /> Retry
              </button>
            </div>
          )}
          {!loading && !error && sorted.length === 0 && (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground/70">
              No entities yet — create your first company.
            </div>
          )}
          {sorted.length > 0 && (
            // IN-04 named this row in his own words: "we have in the dropdown an ALL above which
            // is showing the XX … hence clear that this is the number of all". It is a LABEL, not
            // a fourth click target, because the items behind it are read per entity — the queue
            // endpoint takes one entity id, so an "all companies" ITEM list does not exist and
            // this row must not promise one. Each entity row below carries its own count.
            <div
              data-testid="attention-all-companies"
              className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs font-medium text-muted-foreground/80"
            >
              <span>All companies</span>
              <NeedsYouBadge count={totalOutstanding} tone="neutral" noun="across all your companies" />
            </div>
          )}
          {sorted.length > 0 && sorted.map(renderRow)}
          <DropdownMenuSeparator className="my-1" />
          {/* Create lives here (opens the shared wizard, auto-selects the result) — not as a
              stray button on every page. The "+" is dark on the teal chip (brand pattern). */}
          <DropdownMenuItem
            onSelect={() => setCreateOpen(true)}
            className="flex items-center gap-2"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[#0AB3BF] text-slate-900">
              <Plus className="h-3.5 w-3.5 text-slate-900" strokeWidth={3} />
            </span>
            <span className="truncate font-medium">New company</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Wizard_Entity_Onboarding
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onEntityCreated={(e) => {
          setCreateOpen(false);
          if (e?.entity_id) {
            setCurrentEntityId(e.entity_id);
            // A fresh call after create refetches, so the new company appears in the list.
            loadEntities();
          }
        }}
      />
    </>
  );
}

/**
 * THE RED DOT MOVED ONE STEP FURTHER — PO 2026-09-28, and this is the record of where it went.
 *
 * `D-144-018` had just taken the number out of the trigger button and made it a sibling, so that
 * following the number landed on the items it counts. The PO then named the pair that produced as
 * the defect: *"all 60 [14] … ITS CONFUSING … its the other way around … the ones which are
 * related to the entity must be in the entity dropdown … move the 'all 60' to the outside where
 * the current red dot is and instead call it '60 total'"*.
 *
 * So the connector now carries the PORTFOLIO TOTAL only — neutral, with the word `total` after the
 * number — and the company's own count keeps two homes that are each about ONE company: its row in
 * the dropdown, and the Entity Review row in the rail.
 *
 * WHY THE RAIL ROW IS THE DOOR NOW: it is an anchor to `/entity-review` (the address left the
 * `/harness/*` namespace on 2026-10-05 — P042 `D-042-016`) for the company
 * you are standing in, so `persona_Maya-Founder.md`'s *"she does not go looking"* still holds —
 * the number she follows is one click from the items it counts, in the place she already looks.
 * The redundant `EntityAttentionDoor` component was removed with this move rather than left
 * mounted nowhere (the defect P136 keeps finding in this codebase).
 *
 * AND WHY THE SUM IS NOT A DOOR: the deleted component's own honest limit still stands — no
 * surface lists every company's items at once, so a number across all companies is a display.
 * Making it a link would promise a page that does not exist.
 */
